import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchMe } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    // Returning from Google OAuth: AuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("sabzy_token");
    const isAdmin = localStorage.getItem("sabzy_is_admin") === "true";
    if (!token) {
      setLoading(false);
      return;
    }
    if (isAdmin) {
      setAdmin({ email: localStorage.getItem("sabzy_admin_email") || "admin" });
      setLoading(false);
      return;
    }
    fetchMe()
      .then((res) => setUser(res.data))
      .catch(() => {
        // Token invalid/expired — clear it silently
        localStorage.removeItem("sabzy_token");
      })
      .finally(() => setLoading(false));
  }, []);

  const loginAs = useCallback((token, userObj) => {
    localStorage.setItem("sabzy_token", token);
    localStorage.setItem("sabzy_is_admin", "false");
    setUser(userObj);
    setAdmin(null);
  }, []);

  const loginAsAdmin = useCallback((token, adminObj) => {
    localStorage.setItem("sabzy_token", token);
    localStorage.setItem("sabzy_is_admin", "true");
    localStorage.setItem("sabzy_admin_email", adminObj.email);
    setAdmin(adminObj);
    setUser(null);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("sabzy_token");
    localStorage.removeItem("sabzy_is_admin");
    localStorage.removeItem("sabzy_admin_email");
    setUser(null);
    setAdmin(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await fetchMe();
      setUser(res.data);
      return res.data;
    } catch (e) {
      console.warn("refreshUser failed:", e?.response?.status || e?.message);
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({ user, admin, loading, loginAs, loginAsAdmin, logout, refreshUser }),
    [user, admin, loading, loginAs, loginAsAdmin, logout, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
